/**
 * Builds the option list for the catalog's "Uploader Name" filter.
 *
 * UI-63: this used to be derived from the loaded `documents` array, but that
 * array holds only the pages fetched so far — the catalog list is server
 * paginated. The dropdown therefore listed whichever uploaders happened to
 * appear in the pages you had already scrolled through, growing as you
 * scrolled, so it could never be used to find someone whose documents you had
 * not already reached. Two reporters hit this independently ("limited to a
 * dropdown of three names"; "only per page not total").
 *
 * The organization directory is the correct source: it is the full roster and
 * does not depend on scroll position.
 *
 * Known limit: the directory lists *current* members, so an uploader who has
 * since left is recovered only from loaded documents and therefore still
 * appears once one of their pages loads. Closing that gap needs a server-side
 * "distinct uploaders" endpoint. The recovery is strictly additive — paging
 * never removes or renames an option that was already offered — and no current
 * member is ever missing, which is the defect that was reported.
 */

/** The subset of a document row this derivation needs. */
export interface UploaderSource {
  user_id?: string | null
  uploader_name?: string | null
}

/**
 * @param directory  user_id → display name, from the organization directory.
 * @param documents  Loaded document rows, used only to recover uploaders the
 *                   directory no longer lists (e.g. a removed member) so their
 *                   documents stay filterable.
 * @returns `[userId, name]` pairs sorted by name.
 */
export function buildUploaderOptions(
  directory: Map<string, string>,
  documents: readonly UploaderSource[],
): [string, string][] {
  const map = new Map<string, string>()

  directory.forEach((name, userId) => {
    const trimmed = name?.trim()
    if (userId && trimmed) map.set(userId, trimmed)
  })

  for (const doc of documents) {
    if (!doc.user_id || map.has(doc.user_id)) continue
    const name = doc.uploader_name?.trim()
    // Entries with no resolvable name are skipped rather than labelled with a
    // raw user id — a UUID is not something anyone can recognise or search by.
    if (name) map.set(doc.user_id, name)
  }

  return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]))
}

/**
 * Case-insensitive substring narrowing for the uploader search field.
 *
 * Matches the display name only, deliberately. Ids are not user-facing text, and
 * matching them would let a typed fragment of a UUID surface an unrelated person
 * — see the `does not match against the user id` test.
 */
export function filterUploaderOptions(
  options: readonly [string, string][],
  query: string,
): [string, string][] {
  const q = query.trim().toLowerCase()
  if (!q) return options as [string, string][]
  return options.filter(([, name]) => name.toLowerCase().includes(q))
}
